// Логове (04 §7) — filters, field query, saved queries, SQL console, retention notice.
//
// Two things this screen refuses to blur. Browser-sourced rows are labelled on every line, because
// the ingest endpoint is public and a browser claim is not a server fact. And the retention notice
// is always visible: debug and info survive 7 days, warn 30, error and critical a year (05 §5), so
// "there is nothing here" means something different depending on what you were looking for.

import { useState } from 'react';
import {
  EMPTY_LOG_FILTERS,
  useLogQuery,
  useLogs,
  useSavedQueries,
  useSaveQuery,
  type LogFilters,
  type LogLevel,
  type LogRow,
} from './useLogs.js';
import './logs.css';

const LEVELS: LogLevel[] = ['debug', 'info', 'warn', 'error', 'critical'];

const RANGES: Array<{ hours: number; label: string }> = [
  { hours: 1, label: 'последен час' },
  { hours: 24, label: '24 часа' },
  { hours: 24 * 7, label: '7 дни' },
  { hours: 24 * 30, label: '30 дни' },
];

function Row({ row }: { row: LogRow }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasContext = Object.keys(row.context ?? {}).length > 0;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(row.context, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <tr
        className={`log log--${row.level} ${open ? 'log--open' : ''}`}
        onClick={() => setOpen(!open)}
      >
        <td className="log__when">
          <span className="log__expander">{hasContext ? (open ? '▾' : '▸') : '•'}</span>
          {new Date(row.happened_at).toLocaleTimeString('bg-BG', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}
        </td>
        <td>
          <span className={`log__level log__level--${row.level}`}>{row.level}</span>
        </td>
        <td>
          {row.source === 'browser' ? (
            <span className="log__source" title="Докладвано от браузър — непотвърдено">
              браузър
            </span>
          ) : (
            <span className="log__source log__source--server">сървър</span>
          )}
        </td>
        <td className="log__cat">{row.category ? <code>{row.category}</code> : '—'}</td>
        <td className="log__msg">{row.message}</td>
        <td className="log__site">#{row.site_id}</td>
      </tr>
      {open && hasContext ? (
        <tr className="log__ctxrow">
          <td colSpan={6}>
            <div className="log__ctxbox">
              <div className="log__ctxhead">
                <span>Контекст на събитието (JSON)</span>
                <button type="button" className="log__copybtn" onClick={handleCopy}>
                  {copied ? '✓ Копирано' : 'Копирай'}
                </button>
              </div>
              <pre className="log__ctx">{JSON.stringify(row.context, null, 2)}</pre>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Console() {
  const run = useLogQuery();
  const save = useSaveQuery();
  const { data: saved = [] } = useSavedQueries();
  const [sql, setSql] = useState(
    "select level, count(*) from site_logs where happened_at > now() - interval '7 days' group by level",
  );

  const rows = run.data ?? [];
  const columns = rows.length > 0 ? Object.keys(rows[0] ?? {}) : [];

  return (
    <section className="console">
      <div className="console__head">
        <div className="console__title-row">
          <span className="console__dot" />
          <h3 className="console__title">SQL конзола за анализ</h3>
        </div>
        <select
          className="console__select"
          value=""
          aria-label="Запазени заявки"
          onChange={(event) => {
            const chosen = saved.find((entry) => String(entry.id) === event.target.value);
            if (chosen?.filter_data.sql) setSql(chosen.filter_data.sql);
          }}
        >
          <option value="">Запазени заявки…</option>
          {saved.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </div>

      <textarea
        className="console__sql"
        value={sql}
        onChange={(event) => setSql(event.target.value)}
        rows={3}
        spellCheck={false}
      />

      <div className="console__bar">
        <button
          type="button"
          className="console__run-btn"
          onClick={() => run.mutate(sql)}
          disabled={run.isPending}
        >
          {run.isPending ? 'Изпълнява се…' : 'Изпълни SQL →'}
        </button>
        <button
          type="button"
          className="console__save-btn"
          onClick={() => {
            const name = window.prompt('Име на заявката?');
            if (name) save.mutate({ name, sql });
          }}
        >
          Запази заявка
        </button>
        <span className="console__limits">
          само SELECT · site_logs, site_events, site_checks_daily, alerts · 5 сек · макс 5000 реда
        </span>
      </div>

      {run.isError ? <p className="console__err">{(run.error as Error).message}</p> : null}

      {run.isSuccess ? (
        rows.length === 0 ? (
          <p className="console__empty">Заявката завърши успешно, но не върна редове.</p>
        ) : (
          <div className="console__result">
            <table className="logs__table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, index) => (
                  <tr key={index}>
                    {columns.map((column) => (
                      <td key={column}>
                        {r[column] === null || r[column] === undefined
                          ? ''
                          : typeof r[column] === 'object'
                            ? JSON.stringify(r[column])
                            : String(r[column])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="console__count">{rows.length} резултата</p>
          </div>
        )
      ) : null}
    </section>
  );
}

function Filters({
  filters,
  onChange,
  count,
}: {
  filters: LogFilters;
  onChange: (next: LogFilters) => void;
  count: number;
}) {
  const set = <K extends keyof LogFilters>(key: K, value: LogFilters[K]): void =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="logs__filters">
      <div className="logs__filter-row">
        <input
          type="search"
          className="logs__search"
          placeholder="Търсене в съобщението…"
          value={filters.search}
          onChange={(e) => set('search', e.target.value)}
        />

        <select
          className="logs__select"
          value={filters.level}
          onChange={(e) => set('level', e.target.value as LogLevel | '')}
        >
          <option value="">всички нива</option>
          {LEVELS.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </select>

        <select
          className="logs__select"
          value={filters.source}
          onChange={(e) => set('source', e.target.value as LogFilters['source'])}
        >
          <option value="">всякакъв източник</option>
          <option value="server">сървър</option>
          <option value="browser">браузър</option>
        </select>

        <select
          className="logs__select"
          value={filters.sinceHours}
          onChange={(e) => set('sinceHours', Number(e.target.value))}
        >
          {RANGES.map((range) => (
            <option key={range.hours} value={range.hours}>
              {range.label}
            </option>
          ))}
        </select>

        <button type="button" className="logs__clear" onClick={() => onChange(EMPTY_LOG_FILTERS)}>
          Изчисти
        </button>

        <span className="logs__count-badge">{count} записа</span>
      </div>

      <div className="logs__context-row">
        <span className="logs__ctx-label">Контекстен филтър:</span>
        <input
          type="text"
          className="logs__ctx-input"
          placeholder="Ключ (напр. url)"
          value={filters.contextKey}
          onChange={(e) => set('contextKey', e.target.value)}
        />
        <span className="logs__ctx-sep">=</span>
        <input
          type="text"
          className="logs__ctx-input"
          placeholder="Стойност"
          value={filters.contextValue}
          onChange={(e) => set('contextValue', e.target.value)}
        />
      </div>
    </div>
  );
}

export function LogsPage() {
  const [filters, setFilters] = useState<LogFilters>(EMPTY_LOG_FILTERS);
  const { data: rows = [], isLoading, error } = useLogs(filters);

  return (
    <div className="logs">
      <Filters filters={filters} onChange={setFilters} count={rows.length} />

      <div className="logs__retention-bar">
        <span className="logs__retention-icon">⏱</span>
        <span className="logs__retention-text">
          <strong>Политика за съхранение:</strong> debug & info (7 дни) · warn (30 дни) · error &
          critical (365 дни). По-старите записи се архивират и изтриват автоматично.
        </span>
      </div>

      {isLoading ? <p className="logs__msg">Зареждане на логове…</p> : null}
      {error ? <p className="logs__msg logs__msg--err">{(error as Error).message}</p> : null}

      {!isLoading && rows.length === 0 ? (
        <div className="logs__empty-card">
          <p className="logs__msg">
            Няма записи за избраните филтри. Логовете се получават в реално време от клиентските
            сайтове чрез мониторинг снипета.
          </p>
        </div>
      ) : (
        <div className="logs__wrap">
          <table className="logs__table">
            <thead>
              <tr>
                <th>час</th>
                <th>ниво</th>
                <th>източник</th>
                <th>категория</th>
                <th>съобщение</th>
                <th>сайт</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Console />
    </div>
  );
}
